/**
 * FilterBar primitives.
 *
 * Canonical filter-bar pattern used by table surfaces across the app.
 * The original lived inline in BookingsTable (Frontdesk → Bookings →
 * "All bookings" tab); promoting it here keeps every table's filter
 * row visually identical: a search input on the left, chip-dropdowns
 * after it, "Clear all" dashed chip, then a right-aligned action
 * cluster (Export, "+ New …" CTA, …).
 *
 * Usage
 * -----
 *
 *   <FilterBar>
 *     <FilterBar.Search value={q} onChange={setQ} placeholder="…" />
 *
 *     <Popover>
 *       <PopoverTrigger asChild>
 *         <FilterChip label="Status" summary="any" active={false} />
 *       </PopoverTrigger>
 *       <PopoverContent>…</PopoverContent>
 *     </Popover>
 *
 *     <FilterBar.ClearAll onClick={reset} disabled={!hasFilters} />
 *
 *     <FilterBar.Spacer />
 *
 *     <Button variant="outline" size="sm">…</Button>
 *     <Button size="sm">+ New …</Button>
 *   </FilterBar>
 *
 * The chip uses `ocean-700` accent when active so it reads as a
 * "selected pill" against the sand background — matches the bookings
 * pattern exactly.
 */

import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Input } from '@/theme/components/ui/input';

interface FilterBarProps {
  children: React.ReactNode;
  className?: string;
}

function FilterBarRoot({ children, className }: FilterBarProps) {
  return (
    <div className={cn('mb-4 flex flex-wrap items-center gap-2', className)}>
      {children}
    </div>
  );
}

interface FilterSearchProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
}

function FilterSearch({ value, onChange, placeholder, className }: FilterSearchProps) {
  return (
    <div className={cn('relative min-w-[220px] flex-1', className)}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-md border border-sand-200 bg-sand-50 pl-10 text-[12.5px]"
      />
    </div>
  );
}

interface FilterChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  label: string;
  /** Compact tail summary (e.g. "any" / "open · 3"). */
  summary: string;
  active: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Pill-style trigger for a filter dropdown. Wrap with shadcn Popover
 * (or any other trigger) — this component only renders the visual.
 */
export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  ({ label, summary, active, onClick, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      {...rest}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-[5px] text-[12px] font-medium transition-colors',
        active
          ? 'border-ocean-700 bg-ocean-700 text-white hover:bg-ocean-800'
          : 'border-sand-200 bg-card text-sand-900 hover:border-sand-400 hover:bg-sand-50',
        rest.className,
      )}
    >
      <span>{label}</span>
      <span aria-hidden="true" className="opacity-60">·</span>
      <span
        className={cn(
          'font-mono text-[11px]',
          active ? 'text-white' : 'text-muted-foreground',
        )}
      >
        {summary}
      </span>
      <ChevronDown className="h-3 w-3 opacity-80" aria-hidden="true" />
    </button>
  ),
);
FilterChip.displayName = 'FilterChip';

interface FilterToggleChipProps {
  on: boolean;
  onClick: () => void;
  tone?: 'warn';
  children: React.ReactNode;
}

/**
 * Rounded on/off filter chip (aria-pressed toggle) — the catalog-chip idiom
 * shared by the Services and Activities hubs. Unlike `FilterChip` (a dropdown
 * trigger), this one toggles a filter directly.
 */
export function FilterToggleChip({ on, onClick, tone, children }: FilterToggleChipProps) {
  const onCls = tone === 'warn'
    ? 'border-sun-500 bg-sun-500 text-white'
    : 'border-coral-600 bg-coral-600 text-white';
  const offCls = tone === 'warn'
    ? 'border-sun-300 bg-card text-sun-800 hover:border-sun-400 dark:text-sun-200'
    : 'border-sand-200 bg-card text-sand-900 hover:border-sand-400';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium',
        on ? onCls : offCls,
      )}
    >
      {children}
    </button>
  );
}

interface ClearAllProps {
  onClick: () => void;
  disabled?: boolean;
  label?: string;
  className?: string;
}

function ClearAll({ onClick, disabled, label, className }: ClearAllProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border border-dashed border-border px-3 py-[5px] text-[12px] font-medium text-muted-foreground transition-colors hover:border-sand-300 hover:bg-sand-50 disabled:opacity-40',
        className,
      )}
    >
      {label ?? t('common.clearAll', 'Clear all')}
    </button>
  );
}

function Spacer() {
  return <div className="ml-auto" />;
}

/**
 * Shared row item: a small, simple option button used inside the
 * popover content of a chip filter. Matches the bookings dropdown
 * row exactly so every chip dropdown looks the same.
 */
interface FilterOptionProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}

export function FilterOption({ active, onClick, children, className }: FilterOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-[12.5px] hover:bg-sand-50',
        active && 'bg-sand-50',
        className,
      )}
    >
      <span className="flex items-center gap-2">{children}</span>
      {active ? <span className="font-mono text-[11px] text-sage-700">✓</span> : null}
    </button>
  );
}

/** Compose the namespaced API used in markup. */
export const FilterBar = Object.assign(FilterBarRoot, {
  Search: FilterSearch,
  ClearAll,
  Spacer,
});
